import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { AlertTriangle, CalendarClock, CalendarX, Clock, Eye, EyeOff, FileWarning, Upload } from 'lucide-react'
import { useEffect, useState } from 'react'
import { PersonScopeControl } from '@/components/scope/PersonScopeControl'
import { MonthBreakdown } from '@/components/dashboard/MonthBreakdown'
import { Legend, MonthlyChart } from '@/components/dashboard/MonthlyChart'
import { Sparkline } from '@/components/dashboard/Sparkline'
import { Tooltip } from '@/components/ui/Tooltip'
import { Card, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { PageHeader } from '@/components/ui/PageHeader'
import { PrivateAmount } from '@/components/ui/PrivateAmount'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { useAuth } from '@/lib/auth'
import { useBlurAmounts } from '@/lib/blur-amounts'
import { toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { useCountUp } from '@/lib/useCountUp'
import { useMonthSelection } from '@/lib/use-month-selection'
import { cn } from '@/lib/utils'
import { stateLabel, waitingOn, type PayoutViewer } from '@/lib/payout-waiting'
import { useDashboard, usePersonalDashboard } from '@/lib/report-api'
import type { DashboardResponse, ViewScope } from '@/lib/report-types'

/**
 * Het trendlijntje naast een KPI. Op smalle schermen valt het weg: daar is de
 * ruimte op na het bedrag, en een lijntje dat over de cijfers of door de
 * hairline heen loopt zegt minder dan niets. Op groter formaat vertelt de
 * tooltip waar de lijn over gaat.
 */
function TrendLine({ data, hint }: { data: number[]; hint?: string }) {
  return (
    <Tooltip content={hint}>
      <div className="hidden shrink-0 opacity-80 md:block">
        <Sparkline data={data} />
      </div>
    </Tooltip>
  )
}

function stagger(index: number) {
  return { animationDelay: `${index * 70}ms` }
}

/**
 * De schakelaar naast de jaarkeuze, alleen zichtbaar als de voorkeur aan staat:
 * zonder blur valt er niets te tonen of te verbergen.
 *
 * Dit is ook de toetsenbord-ingang. De bedragen zelf zijn wel klikbaar maar niet
 * focusbaar: twintig extra tabstops op een dashboard weegt niet op tegen een
 * affordance die hier al volwaardig zit.
 */
function AmountsToggle() {
  const { enabled, unlocked, expiresAt, toggle } = useBlurAmounts()
  const [, retick] = useState(0)

  // Zonder deze tik loopt de resterende tijd in de tooltip achter tot er toevallig
  // iets anders herrendert.
  useEffect(() => {
    if (!unlocked) return
    const id = setInterval(() => retick((n) => n + 1), 30_000)
    return () => clearInterval(id)
  }, [unlocked])

  if (!enabled) return null

  const minutes = expiresAt === null ? 0 : Math.max(1, Math.round((expiresAt - Date.now()) / 60_000))

  return (
    <Tooltip
      content={
        unlocked
          ? `Bedragen zichtbaar, nog ${minutes} min`
          : 'Bedragen staan onscherp. Ga over een bedrag om het te lezen, of klik erop om alles te tonen.'
      }
    >
      <button
        type="button"
        onClick={toggle}
        aria-pressed={unlocked}
        aria-label={unlocked ? 'Bedragen verbergen' : 'Bedragen tonen'}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-hairline bg-surface text-ink-faint transition-colors hover:bg-fill-1 hover:text-ink"
      >
        {unlocked ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
      </button>
    </Tooltip>
  )
}

function AnimatedValue({ target, format }: { target: number; format: (value: number) => string }) {
  const value = useCountUp(target)
  return <>{format(value)}</>
}

export function DashboardPage() {
  const [year, setYear] = useState(() => new Date().getFullYear())
  const routeSearch = useSearch({ strict: false }) as { scope?: string; person?: string }
  const navigate = useNavigate()
  const auth = useAuth()
  const role = auth.data?.user.role
  const canSeeCompany = role === 'ADMIN' || role === 'PARTNER'
  const scope: ViewScope = routeSearch.scope === 'company' ? 'company' : 'personal'
  const activeScope: ViewScope = canSeeCompany && scope === 'company' ? 'company' : 'personal'
  // Wisselen tussen Persoonlijk en Bedrijf laat de gekozen collega ongemoeid: die blijft
  // in de eerste knop staan tot je expliciet "Mijzelf" of iemand anders kiest.
  function setScope(next: ViewScope) {
    navigate({
      to: '/',
      search: (prev) => ({ ...prev, scope: next === 'personal' ? undefined : next }),
      replace: true,
    })
  }
  function setPerson(next: string | undefined) {
    navigate({ to: '/', search: (prev) => ({ ...prev, scope: undefined, person: next }), replace: true })
  }

  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear + 1 - i)

  // Ook op de bedrijfsweergave opgehaald: anders verdwijnt de personenkeuze uit de
  // knoppenrij zodra je naar Bedrijf wisselt, en komt hij bij terugkeer weer opzetten.
  const personalQuery = usePersonalDashboard(year, routeSearch.person)
  const personal = personalQuery.data
  // Een lege people-lijst betekent dat je alleen jezelf mag zien, dus dan kijk je per
  // definitie naar je eigen dashboard; zonder die tak zou een onderaannemer zichzelf
  // als "een ander" te zien krijgen zodra de data binnen is.
  const self = personal?.people.find((person) => person.isSelf)
  const viewingSelf = personal === undefined || self === undefined || personal.person.id === self.id
  const description =
    activeScope === 'company'
      ? 'Omzet, openstaand en signalen die aandacht vragen.'
      : viewingSelf
        ? 'Jouw omzet, openstaande uitbetalingen en eigen signalen.'
        : `Omzet, uitbetalingen en signalen van ${personal?.person.displayName ?? ''}.`

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description={description}
        actions={
          <div className="flex items-center gap-2">
            {canSeeCompany ? (
              <PersonScopeControl
                scope={activeScope}
                people={personal?.people ?? []}
                personId={routeSearch.person}
                onScope={setScope}
                onPerson={setPerson}
                personalLabel="Mijzelf"
                companyLabel="Bedrijf"
                menuTitle="Bekijk het dashboard van"
                label="Weergave"
              />
            ) : null}
            <Select
              value={String(year)}
              onValueChange={(v) => setYear(Number(v))}
              options={yearOptions.map((y) => ({ value: String(y), label: String(y) }))}
              className="w-24"
            />
            <AmountsToggle />
          </div>
        }
      />

      {activeScope === 'personal' ? (
        <PersonalView query={personalQuery} year={year} viewingSelf={viewingSelf} />
      ) : (
        <CompanyView year={year} />
      )}
    </div>
  )
}

function PersonalView({
  query,
  year,
  viewingSelf,
}: {
  query: ReturnType<typeof usePersonalDashboard>
  year: number
  viewingSelf: boolean
}) {
  const data = query.data
  const { selectedMonth, selectedPoint, selectMonth, panelRef, expanded, setExpanded } = useMonthSelection(data?.monthlyShare)

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => query.refetch()} />
  }

  // Kijk je bij een ander, dan is "wacht op jouw inkoopfactuur" niet waar; dat is
  // precies het onderscheid dat PayoutViewer maakt.
  const viewer: PayoutViewer = viewingSelf ? 'self' : 'admin'

  const monthsElapsed = year === new Date().getFullYear() ? new Date().getMonth() + 1 : 12
  const monthlyValues = data ? data.monthlyShare.slice(0, monthsElapsed).map((m) => toNumber(m.current)) : []
  const cumulativeValues = monthlyValues.reduce<number[]>((acc, v) => {
    acc.push((acc.at(-1) ?? 0) + v)
    return acc
  }, [])

  const pipeline = data
    ? [
        { label: stateLabel('awaiting-client', viewer), amount: toNumber(data.openPayout.awaitingClientPayment), action: false },
        { label: stateLabel('awaiting-incoming', viewer), amount: toNumber(data.openPayout.missingIncomingInvoice), action: true },
        { label: stateLabel('awaiting-approval', viewer), amount: toNumber(data.openPayout.awaitingApproval), action: false },
        { label: stateLabel('awaiting-payout', viewer), amount: toNumber(data.openPayout.readyForPayout), action: false },
      ]
    : []

  return (
    <>
      <div className="grid grid-cols-1 overflow-hidden rounded-card bg-surface shadow-card md:grid-cols-2">
        <div className="flex flex-col gap-3 p-5">
          <Tooltip
            content={
              viewingSelf
                ? "Jouw aandeel in de omzet dit kalenderjaar: de som van je verdelingsregels op facturen vanaf status Approved, geteld op factuurdatum (niet op uitbetaaldatum). Concepten tellen niet mee. Dit kan afwijken van 'Uitbetaald' op Mijn uitbetalingen, want een factuur van vorig jaar die dit jaar wordt uitbetaald telt daar wel mee, maar hoorde bij de omzet van vorig jaar."
                : "Het aandeel van deze persoon in de omzet dit kalenderjaar: de som van diens verdelingsregels op facturen vanaf status Approved, geteld op factuurdatum (niet op uitbetaaldatum). Concepten tellen niet mee."
            }
          >
            <span className="label-caps">{viewingSelf ? 'Jouw omzet YTD' : 'Omzet YTD'}</span>
          </Tooltip>
          <div className="flex min-w-0 items-end justify-between gap-3">
            {data ? (
              <PrivateAmount className="money text-[22px] font-semibold leading-none text-ink">
                <AnimatedValue target={toNumber(data.ytdShare)} format={formatEuros} />
              </PrivateAmount>
            ) : (
              <Skeleton className="h-6 w-24" />
            )}
            {data ? (
              <TrendLine
                data={cumulativeValues}
                hint={`Cumulatief verloop van ${viewingSelf ? 'jouw' : 'deze'} omzet dit jaar, per maand.`}
              />
            ) : null}
          </div>
        </div>
        <div className="flex flex-col gap-3 border-t border-hairline p-5 md:border-l md:border-t-0">
          <Tooltip content="Het deel van je verdeling dat nog niet is uitgekeerd, op facturen vanaf status Approved. Concepten tellen niet mee; dit volgt de uitbetaal-pipeline hieronder.">
            <span className="label-caps">Nog uit te keren</span>
          </Tooltip>
          {data ? (
            <PrivateAmount className="money text-[22px] font-semibold leading-none text-ink">
              <AnimatedValue target={toNumber(data.openPayout.total)} format={formatEuros} />
            </PrivateAmount>
          ) : (
            <Skeleton className="h-6 w-24" />
          )}
        </div>
      </div>
      <p className="text-xs text-ink-faint">Bedragen excl. btw</p>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="animate-fade-up lg:col-span-2" style={stagger(1)}>
          <CardHeader
            title={viewingSelf ? 'Jouw omzet per maand' : 'Omzet per maand'}
            subtitle={selectedMonth === null ? `${year} vs ${year - 1} · kies een maand` : `${year} vs ${year - 1}`}
            action={<Legend year={year} points={data?.monthlyShare} />}
          />
          {data ? (
            <MonthlyChart points={data.monthlyShare} year={year} selectedMonth={selectedMonth} onSelectMonth={selectMonth} />
          ) : (
            <Skeleton className="mt-6 h-56 w-full" />
          )}
        </Card>

        <Card ref={panelRef} className="animate-fade-up" style={stagger(2)}>
          {selectedPoint ? (
            <MonthBreakdown
              key={`${year}-${selectedPoint.month}`}
              point={selectedPoint}
              year={year}
              dimension="client"
              backLabel="Uitbetaal-pipeline"
              onBack={() => selectMonth(null)}
              expanded={expanded}
              onExpandedChange={setExpanded}
            />
          ) : (
            <div className="animate-fade-in">
              <CardHeader title="Uitbetaal-pipeline" subtitle="Openstaand per fase" />
              {data ? (
                <div className="mt-4 space-y-3">
                  {pipeline.map((row) => (
                    <div key={row.label} className="flex items-center justify-between gap-3 text-sm">
                      <span className={cn('text-ink-soft', row.action && row.amount > 0 && 'font-medium text-status-approval')}>
                        {row.label}
                      </span>
                      <PrivateAmount className="money font-medium text-ink">{formatEuros(row.amount)}</PrivateAmount>
                    </div>
                  ))}
                  <div className="mt-3 space-y-2 border-t border-hairline pt-3">
                    <SignalRow icon={Clock} label={viewingSelf ? 'Eigen ontbrekende uren (vorige week)' : 'Ontbrekende uren (vorige week)'} count={data.signals.missingHours} tone="amber" />
                    <SignalRow icon={CalendarX} label="Weken nog niet afgesloten" count={data.signals.openWeeks} tone="amber" />
                  </div>
                </div>
              ) : (
                <Skeleton className="mt-4 h-40 w-full" />
              )}
            </div>
          )}
        </Card>
      </div>

      <Card className="animate-fade-up" style={stagger(3)}>
        <CardHeader
          title="Openstaande uitbetalingen"
          subtitle={viewingSelf ? 'Jouw regels die nog niet zijn uitgekeerd' : 'Regels die nog niet zijn uitgekeerd'}
          action={
            // /mijn-uitbetalingen toont altijd je eigen regels, dus bij een ander wijst
            // die link naar de verkeerde data; daar is het beheeroverzicht de plek.
            viewingSelf ? (
              <Link to="/mijn-uitbetalingen" className="text-sm text-accent-700 hover:underline">
                Naar mijn uitbetalingen
              </Link>
            ) : (
              <Link to="/uitbetalingen" className="text-sm text-accent-700 hover:underline">
                Naar alle uitbetalingen
              </Link>
            )
          }
        />
        {data ? (
          data.openLines.length === 0 ? (
            <p className="mt-4 text-sm text-ink-faint">Niets openstaand. Alles is uitgekeerd.</p>
          ) : (
            <div className="mt-4 divide-y divide-hairline">
              {data.openLines.map((line) => (
                <div key={line.lineId} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink">
                      {line.fullNumber} · {line.clientName}
                    </span>
                    <span className="block text-xs text-ink-faint">
                      {waitingOn({ invoiceStatus: line.invoiceStatus, payoutStage: line.stage, autoSettle: line.autoSettle }, viewer).label}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    {viewingSelf && line.clientPaid && !line.hasIncomingInvoice ? (
                      <Link to="/mijn-uitbetalingen" className="inline-flex items-center gap-1 text-xs font-medium text-accent-700 hover:underline">
                        <Upload className="h-3.5 w-3.5" />
                        Inkoopfactuur uploaden
                      </Link>
                    ) : null}
                    <PrivateAmount className="money font-medium text-ink">{formatEuros(toNumber(line.amount))}</PrivateAmount>
                  </span>
                </div>
              ))}
            </div>
          )
        ) : (
          <Skeleton className="mt-4 h-40 w-full" />
        )}
      </Card>
    </>
  )
}

function CompanyView({ year }: { year: number }) {
  const dashboardQuery = useDashboard(year)
  const data = dashboardQuery.data
  const { selectedMonth, selectedPoint, selectMonth, panelRef, expanded, setExpanded } = useMonthSelection(data?.monthlyRevenue)

  const ytd = data ? data.monthlyRevenue.reduce((s, m) => s + toNumber(m.current), 0) : 0
  const thisMonth = data ? toNumber(data.monthlyRevenue[new Date().getMonth()]?.current) : 0

  const monthsElapsed = new Date().getMonth() + 1
  const monthlyValues = data ? data.monthlyRevenue.slice(0, monthsElapsed).map((m) => toNumber(m.current)) : []
  const cumulativeValues = monthlyValues.reduce<number[]>((acc, v) => {
    acc.push((acc.at(-1) ?? 0) + v)
    return acc
  }, [])

  if (dashboardQuery.isError) {
    return <ErrorState error={dashboardQuery.error} onRetry={() => dashboardQuery.refetch()} />
  }

  return (
    <>
      <div className="grid grid-cols-1 overflow-hidden rounded-card bg-surface shadow-card sm:grid-cols-2 xl:grid-cols-3">
        {[
          { label: 'Omzet deze maand', numeric: thisMonth, format: formatEuros, spark: monthlyValues, hint: 'Omzet per maand, januari tot en met deze maand.', definition: 'De omzet deze kalendermaand op factuurniveau (subtotaal, niet verdeeld), op facturen vanaf status Approved. Concepten tellen niet mee.' },
          { label: 'Openstaand', numeric: data ? toNumber(data.outstanding.total) : 0, format: formatEuros, spark: undefined, hint: undefined, definition: 'Het subtotaal van verzonden of geescaleerde facturen die nog niet zijn betaald. Concepten, geparkeerde en al betaalde facturen tellen niet mee, ongeacht periode.' },
          { label: 'Omzet YTD', numeric: ytd, format: formatEuros, spark: cumulativeValues, hint: 'Cumulatief verloop van de omzet dit jaar, per maand.', definition: 'De omzet dit kalenderjaar op factuurniveau (subtotaal, niet verdeeld), op facturen vanaf status Approved. Concepten tellen niet mee.' },
        ].map((stat, i) => (
          <div
            key={stat.label}
            className={cn(
              // Mobiel staan de cellen onder elkaar, met een bovenrand als scheiding.
              // Vanaf sm twee kolommen (de derde over de volle breedte), vanaf xl drie.
              'flex min-w-0 flex-col gap-3 overflow-hidden p-5',
              i > 0 && 'border-t border-hairline',
              i % 2 === 1 && 'sm:border-l sm:border-hairline sm:border-t-0',
              i >= 2 && 'sm:col-span-2 xl:col-span-1',
              i > 0 && 'xl:border-l xl:border-hairline xl:border-t-0',
            )}
          >
            <Tooltip content={stat.definition}>
              <span className="label-caps">{stat.label}</span>
            </Tooltip>
            <div className="flex min-w-0 items-end justify-between gap-3">
              {data ? (
                <PrivateAmount className="money text-[22px] font-semibold leading-none text-ink">
                  <AnimatedValue target={stat.numeric} format={stat.format} />
                </PrivateAmount>
              ) : (
                <Skeleton className="h-6 w-24" />
              )}
              {data && stat.spark ? <TrendLine data={stat.spark} hint={stat.hint} /> : null}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-ink-faint">Bedragen excl. btw</p>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="animate-fade-up lg:col-span-2" style={stagger(4)}>
          <CardHeader
            title="Omzet per maand"
            subtitle={selectedMonth === null ? `${year} vs ${year - 1} · kies een maand` : `${year} vs ${year - 1}`}
            action={<Legend year={year} points={data?.monthlyRevenue} />}
          />
          {data ? (
            <MonthlyChart points={data.monthlyRevenue} year={year} selectedMonth={selectedMonth} onSelectMonth={selectMonth} />
          ) : (
            <Skeleton className="mt-6 h-56 w-full" />
          )}
        </Card>

        <Card ref={panelRef} className="animate-fade-up" style={stagger(5)}>
          {selectedPoint ? (
            <MonthBreakdown
              key={`${year}-${selectedPoint.month}`}
              point={selectedPoint}
              year={year}
              dimension="person"
              backLabel="Signalen"
              onBack={() => selectMonth(null)}
              expanded={expanded}
              onExpandedChange={setExpanded}
            />
          ) : (
            <div className="animate-fade-in">
              <CardHeader title="Signalen" subtitle="Wat vraagt actie" />
              {data ? (
                <div className="mt-4 space-y-3">
                  <SignalRow icon={Clock} label="Ontbrekende uren deze week" count={data.signals.missingHours} tone="amber" />
                  <SignalRow icon={CalendarX} label="Weken zonder akkoord (vorige week)" count={data.signals.weeksWithoutApproval} tone="amber" />
                  <SignalRow icon={CalendarClock} label="Weken gewijzigd na akkoord" count={data.signals.weeksChangedAfterApproval} tone="amber" />
                  <SignalRow icon={FileWarning} label="Stale concepten" count={data.signals.staleDrafts} tone="rose" />
                  <SignalRow icon={AlertTriangle} label="Indexaties open" count={data.signals.openIndexations} tone="rose" />
                </div>
              ) : (
                <Skeleton className="mt-4 h-40 w-full" />
              )}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="animate-fade-up" style={stagger(6)}>
          <CardHeader title="Openstaand per klant" subtitle="Met de oudste posten" />
          {data ? (
            <div className="mt-4 space-y-2.5">
              {data.outstanding.perClient.map((client) => (
                <div key={client.clientName} className="flex items-center justify-between text-sm">
                  <span className="text-ink-soft">{client.clientName}</span>
                  <PrivateAmount className="money font-medium text-ink">{formatEuros(toNumber(client.amount))}</PrivateAmount>
                </div>
              ))}
              <div className="mt-3 border-t border-hairline pt-3">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-faint">Oudste openstaand</p>
                {data.outstanding.oldest.map((invoice) => (
                  <Link key={invoice.id} to="/facturen/$id" params={{ id: invoice.id }} className="flex items-center justify-between py-1 text-sm hover:text-accent-700">
                    <span className="text-ink-soft">
                      {invoice.fullNumber}
                      {invoice.daysOpen > 0 ? (
                        <span className={cn('ml-1.5', invoice.needsAttention ? 'text-status-overdue' : 'text-ink-faint')}>
                          {invoice.daysOpen}d
                        </span>
                      ) : null}
                    </span>
                    <PrivateAmount className="money text-ink">{formatEuros(toNumber(invoice.amount))}</PrivateAmount>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <Skeleton className="mt-4 h-40 w-full" />
          )}
        </Card>

        <Card className="animate-fade-up" style={stagger(7)}>
          <CardHeader title="Omzet per persoon" subtitle="YTD, uit de verdelingen" />
          {data ? <PersonBars data={data} /> : <Skeleton className="mt-4 h-40 w-full" />}
        </Card>

        <Card className="animate-fade-up" style={stagger(8)}>
          <CardHeader title="Forecast" subtitle="Verwacht uit recurring" />
          {data ? (
            <div className="mt-4 space-y-2.5">
              {data.forecast.map((month) => (
                <div key={month.month} className="flex items-center justify-between text-sm">
                  <span className="text-ink-soft">{month.month}</span>
                  <PrivateAmount className="money font-medium text-ink">{formatEuros(toNumber(month.amount))}</PrivateAmount>
                </div>
              ))}
            </div>
          ) : (
            <Skeleton className="mt-4 h-40 w-full" />
          )}
        </Card>
      </div>
    </>
  )
}

function PersonBars({ data }: { data: DashboardResponse }) {
  const max = Math.max(1, ...data.revenuePerPerson.map((p) => toNumber(p.amount)))
  return (
    <div className="mt-4 space-y-3">
      {data.revenuePerPerson.map((person, i) => (
        <div key={person.personName}>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="text-ink-soft">{person.personName}</span>
            <PrivateAmount className="money font-medium text-ink">{formatEuros(toNumber(person.amount))}</PrivateAmount>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-fill-2">
            <div
              className="h-full origin-left animate-grow-bar-x rounded-full bg-accent-600/80"
              style={{ width: `${(toNumber(person.amount) / max) * 100}%`, animationDelay: `${i * 60}ms` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

function SignalRow({ icon: Icon, label, count, tone }: { icon: typeof Clock; label: string; count: number; tone: 'amber' | 'rose' | 'slate' }) {
  const toneMap = {
    amber: 'bg-status-approval/10 text-status-approval',
    rose: 'bg-status-overdue/10 text-status-overdue',
    slate: 'bg-fill-2 text-ink-faint',
  } as const
  return (
    <div className="flex items-center gap-3">
      <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${toneMap[tone]}`}>
        <Icon className="h-4 w-4" strokeWidth={1.85} />
      </div>
      <span className="flex-1 text-sm text-ink">{label}</span>
      <span className={`num rounded-pill px-2 py-0.5 text-xs font-medium ${count > 0 ? toneMap[tone] : 'bg-fill-2 text-ink-faint'}`}>{count}</span>
    </div>
  )
}
